import React, { useState, useEffect } from "react";
import {
  getFileStructure,
  getFolderContents,
} from "../../services/fileSystemService";
import { useFolder } from "../../context/FolderContext";

// Helper to build folder tree (only folders, no files)
function buildFolderTree(items, parentId = null) {
  return items
    .filter((item) => item.type === "folder" && item.parentId === parentId)
    .map((item) => ({
      ...item,
      children: buildFolderTree(items, item._id),
    }));
}

// Helper to expand all parent folders in the path
const getExpandedFolders = (path) => {
  const expanded = {};
  path.forEach((folder) => {
    expanded[folder._id] = true;
  });
  return expanded;
};

// Folder tree UI
const FolderTree = ({ tree, selectedId, onSelect, expanded, onToggle }) => {
  if (!tree || tree.length === 0) return null;
  return (
    <ul style={{ padding: 0, margin: 0, listStyle: "none" }}>
      {tree.map((item) => {
        if (item.type !== "folder") return null;
        return (
          <li key={item._id}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "8px 12px",
                margin: "2px 0",
                borderRadius: 10,
                cursor: "pointer",
                transition: "all 0.2s ease",
                background:
                  selectedId === item._id
                    ? "rgba(37,99,235,0.08)"
                    : "transparent",
                color: selectedId === item._id ? "#2563eb" : "#64748b",
                fontWeight: selectedId === item._id ? 600 : 400,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(item); // Pass the entire folder object
              }}
            >
              {item.children && item.children.length > 0 && (
                <span
                  style={{
                    marginRight: 8,
                    fontSize: 16,
                    color: selectedId === item._id ? "#2563eb" : "#64748b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(item._id);
                  }}
                >
                  {expanded[item._id] ? "▼" : "▶"}
                </span>
              )}
              <span style={{ fontSize: 16, marginRight: 8 }}>📁</span>
              <span
                style={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 14,
                }}
              >
                {item.name}
              </span>
            </div>
            {expanded[item._id] &&
              item.children &&
              item.children.length > 0 && (
                <div style={{ marginLeft: 24 }}>
                  <FolderTree
                    tree={item.children}
                    selectedId={selectedId}
                    onSelect={onSelect}
                    expanded={expanded}
                    onToggle={onToggle}
                  />
                </div>
              )}
          </li>
        );
      })}
    </ul>
  );
};

/**
 * FolderSelector component
 * Props:
 *   onSelectFolder(folder) - called when a folder is selected
 */
const FolderSelector = () => {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { selectFolder } = useFolder();

  // Fetch folder structure
  useEffect(() => {
    const fetchStructure = async () => {
      try {
        setLoading(true);
        const response = await getFileStructure();
        const fileStructure = response.data?.items || response.items || [];
        setItems(Array.isArray(fileStructure) ? fileStructure : []);
        setError(null);
      } catch (err) {
        setError(err.message);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStructure();
  }, []);

  const handleToggle = (folderId) => {
    setExpanded((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  };

  // When a folder is selected, update the context
  const handleSelect = (folder) => {
    setSelectedId(folder._id);
    selectFolder(folder);
  };

  const folderTree = buildFolderTree(items);

  if (loading) return <div>Loading folders...</div>;
  if (error) return <div>Error loading folders: {error}</div>;

  return (
    <div style={{ maxHeight: 350, overflowY: "auto", padding: 8 }}>
      <FolderTree
        tree={folderTree}
        selectedId={selectedId}
        onSelect={handleSelect}
        expanded={expanded}
        onToggle={handleToggle}
      />
    </div>
  );
};

export default FolderSelector;
